import { useState, useMemo, useCallback } from 'react';
import { useSupabaseAppointments } from '../../../hooks/useSupabaseAppointments';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { dayNames } from '../../../mocks/appointments';
import WhatsAppConfirmStep from '../../../components/feature/WhatsAppConfirmStep';
import type { WhatsAppBookingDetails } from '../../../utils/whatsapp';

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate();
}

function formatDateISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export default function BookingCalendar() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.05, distance: 36, duration: 900 });
  const {
    appointments, types, slots, blockedDates, loading, addAppointment, createStripeCheckout,
  } = useSupabaseAppointments();

  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [step, setStep] = useState<'calendar' | 'form' | 'success' | 'payment'>('calendar');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmedBooking, setConfirmedBooking] = useState<WhatsAppBookingDetails | null>(null);

  const daysInMonth = useMemo(() => {
    const date = new Date(currentYear, currentMonth + 1, 0);
    return date.getDate();
  }, [currentMonth, currentYear]);

  const firstDayOfMonth = useMemo(() => {
    const date = new Date(currentYear, currentMonth, 1);
    return date.getDay();
  }, [currentMonth, currentYear]);

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentYear, currentMonth, i));
    }
    return days;
  }, [firstDayOfMonth, daysInMonth, currentMonth, currentYear]);

  const isDateBlocked = useCallback((date: Date) => {
    const iso = formatDateISO(date);
    return blockedDates.some((b) => b.date === iso);
  }, [blockedDates]);

  const isPastDate = useCallback((date: Date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d < today;
  }, [today]);

  const isWeekend = useCallback((date: Date) => {
    const d = date.getDay();
    return d === 0 || d === 6;
  }, []);

  const getDaySlots = useCallback((date: Date) => {
    const dow = date.getDay();
    return slots.filter((s) => s.day_of_week === dow && s.is_active);
  }, [slots]);

  const isDayAvailable = useCallback((date: Date) => {
    if (isPastDate(date)) return false;
    if (isDateBlocked(date)) return false;
    if (isWeekend(date)) return false;
    const daySlots = getDaySlots(date);
    if (daySlots.length === 0) return false;
    const iso = formatDateISO(date);
    const dayAppointments = appointments.filter((a) => a.preferred_date === iso && a.status !== 'cancelled');
    return daySlots.some((slot) => {
      const slotStartMin = timeToMinutes(slot.start_time);
      const slotEndMin = timeToMinutes(slot.end_time);
      const overlapping = dayAppointments.filter((appt) => {
        if (!appt.preferred_time) return false;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        return apptStart < slotEndMin && apptEnd > slotStartMin;
      }).length;
      return overlapping < slot.max_bookings;
    });
  }, [isPastDate, isDateBlocked, isWeekend, getDaySlots, appointments, types]);

  /* ── Franjas horarias del dia con disponibilidad real ── */
  const franjasDelDia = useMemo(() => {
    if (!selectedDate) return [];
    const iso = formatDateISO(selectedDate);
    const dayAppointments = appointments.filter((a) => a.preferred_date === iso && a.status !== 'cancelled');
    const daySlots = getDaySlots(selectedDate);

    return daySlots.map((slot) => {
      const slotStartMin = timeToMinutes(slot.start_time);
      const slotEndMin = timeToMinutes(slot.end_time);

      const overlapping = dayAppointments.filter((appt) => {
        if (!appt.preferred_time) return false;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        return apptStart < slotEndMin && apptEnd > slotStartMin;
      });

      const ocupadas = overlapping.length;
      const disponibles = Math.max(0, slot.max_bookings - ocupadas);
      const isFull = disponibles === 0;

      const times: string[] = [];
      const [sh, sm] = slot.start_time.split(':').map(Number);
      const [eh, em] = slot.end_time.split(':').map(Number);
      let h = sh;
      let m = sm;
      while (h < eh || (h === eh && m < em)) {
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        times.push(timeStr);
        m += 30;
        if (m >= 60) { m = 0; h += 1; }
      }

      return {
        slotId: slot.id,
        label: `${slot.start_time} – ${slot.end_time}`,
        isFull,
        disponibles,
        max: slot.max_bookings,
        times,
      };
    });
  }, [selectedDate, appointments, getDaySlots, types]);

  const totalFranjasDisponibles = useMemo(
    () => franjasDelDia.filter((f) => !f.isFull).length,
    [franjasDelDia]
  );

  const goPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleDateSelect = (date: Date) => {
    if (!isDayAvailable(date)) return;
    setSelectedDate(date);
    setSelectedTime('');
    setStep('form');
    setErrorMsg('');
  };

  const selectedType = useMemo(() => types.find((t) => t.name === selectedService), [types, selectedService]);
  const requiresPayment = selectedType?.price !== undefined && selectedType.price > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !selectedService) return;
    setSubmitting(true);
    setErrorMsg('');

    if (requiresPayment && selectedType) {
      const origin = window.location.origin + window.location.pathname;
      const result = await createStripeCheckout({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        service_type: selectedService,
        preferred_date: formatDateISO(selectedDate),
        preferred_time: selectedTime,
        notes: formData.notes || undefined,
        service_name: selectedType.name,
        service_price: selectedType.price,
        service_description: selectedType.description,
        origin_url: origin,
      });
      setSubmitting(false);
      if (result?.url) {
        window.location.href = result.url;
        return;
      }
      setErrorMsg('No se pudo iniciar el pago. Inténtalo de nuevo.');
      return;
    }

    const result = await addAppointment({
      name: formData.name,
      email: formData.email,
      phone: formData.phone || undefined,
      service_type: selectedService,
      preferred_date: formatDateISO(selectedDate),
      preferred_time: selectedTime,
      status: 'pending',
      notes: formData.notes || undefined,
    });
    setSubmitting(false);
    if (result) {
      setConfirmedBooking({
        serviceName: selectedService,
        date: `${selectedDate.getDate()} de ${monthNames[selectedDate.getMonth()]} de ${selectedDate.getFullYear()}`,
        time: selectedTime,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        price: typeof selectedType?.price === 'string' ? parseFloat(selectedType.price) : selectedType?.price,
      });
      setStep('success');
      setFormData({ name: '', email: '', phone: '', notes: '' });
      setSelectedService('');
      setSelectedTime('');
      setSelectedDate(null);
    } else {
      setErrorMsg('No se pudo enviar la reserva. Inténtalo de nuevo.');
    }
  };

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-32">
      <div className="px-6 lg:px-[7%]">
        <div className="max-w-6xl mx-auto" style={style}>
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">
              Consulta de Novia
            </span>
            <h2 className="font-serif text-brand-black text-3xl md:text-4xl font-semibold leading-[1.15] mt-4">
              Reserva tu Cita
              <span className="italic"> para Novia</span>
            </h2>
            <p className="font-sans text-brand-gray-500 text-[15px] leading-relaxed mt-4 max-w-lg mx-auto">
              Agenda una consulta privada de 90 minutos para diseñar tu vestido de novia único. Sin compromiso.
            </p>
          </div>

          {step === 'success' ? (
            <div className="max-w-md mx-auto text-center py-16">
              <div className="w-16 h-16 rounded-full bg-brand-sage flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-brand-black text-2xl" />
              </div>
              <h3 className="font-serif text-brand-black text-2xl font-medium">
                ¡Reserva Enviada!
              </h3>
              <p className="font-sans text-brand-gray-500 text-[15px] leading-relaxed mt-3">
                Hemos recibido tu solicitud. Te contactaremos por email en las próximas 24-48 horas para confirmar los detalles de tu cita de novia.
              </p>
              <WhatsAppConfirmStep booking={confirmedBooking} />
              <div className="mt-8 space-y-3">
                <a
                  href="/mi-reserva"
                  className="inline-block font-sans text-brand-black text-sm tracking-widest uppercase border-b border-brand-black pb-1 hover:text-brand-gray-600 hover:border-brand-gray-600 transition-colors whitespace-nowrap"
                >
                  Consultar mi reserva
                </a>
                <div className="pt-2">
                  <button
                    onClick={() => setStep('calendar')}
                    className="font-sans text-brand-gray-400 text-sm hover:text-brand-black transition-colors whitespace-nowrap"
                  >
                    Reservar otra cita
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-8 lg:gap-12">
              {/* Calendar */}
              <div className="bg-brand-gray-50 rounded-[24px] p-6 md:p-8">
                {/* Month navigation */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={goPrevMonth}
                    className="w-10 h-10 rounded-full bg-white border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <h3 className="font-serif text-brand-black text-xl font-medium">
                    {monthNames[currentMonth]} {currentYear}
                  </h3>
                  <button
                    onClick={goNextMonth}
                    className="w-10 h-10 rounded-full bg-white border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((d) => (
                    <div key={d} className="text-center font-sans text-brand-gray-400 text-xs uppercase tracking-wider py-2">
                      {d.slice(0, 3)}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((date, idx) => {
                    if (!date) {
                      return <div key={`empty-${idx}`} className="aspect-square" />;
                    }
                    const available = isDayAvailable(date);
                    const blocked = isDateBlocked(date);
                    const past = isPastDate(date);
                    const weekend = isWeekend(date);
                    const isSelected = selectedDate && isSameDay(date, selectedDate);

                    let cellClass = 'relative aspect-square rounded-xl flex items-center justify-center font-sans text-sm transition-all ';
                    if (isSelected) {
                      cellClass += 'bg-brand-black text-white font-medium';
                    } else if (available) {
                      cellClass += 'bg-white text-brand-black hover:bg-brand-black hover:text-white cursor-pointer border border-brand-gray-200';
                    } else if (blocked) {
                      cellClass += 'bg-red-50 text-red-300 cursor-not-allowed';
                    } else if (past || weekend) {
                      cellClass += 'text-brand-gray-300 cursor-not-allowed';
                    } else {
                      cellClass += 'text-brand-gray-300 cursor-not-allowed';
                    }

                    return (
                      <button
                        key={date.toISOString()}
                        onClick={() => handleDateSelect(date)}
                        disabled={!available}
                        className={cellClass}
                        title={blocked ? 'Fecha bloqueada' : available ? 'Disponible' : 'No disponible'}
                      >
                        {date.getDate()}
                        {available && !isSelected && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-sage" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-5 mt-6 pt-4 border-t border-brand-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-sage" />
                    <span className="font-sans text-brand-gray-500 text-xs">Disponible</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-black" />
                    <span className="font-sans text-brand-gray-500 text-xs">Seleccionada</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-50 border border-red-200" />
                    <span className="font-sans text-brand-gray-500 text-xs">Bloqueada</span>
                  </div>
                </div>
              </div>

              {/* Form / Times */}
              <div className="space-y-6">
                {loading && (
                  <div className="flex items-center gap-2 text-brand-gray-500 text-sm">
                    <i className="ri-loader-4-line animate-spin" />
                    Cargando disponibilidad...
                  </div>
                )}

                {step === 'calendar' && !selectedDate && (
                  <div className="bg-brand-gray-50 rounded-[24px] p-8 h-full flex flex-col items-center justify-center text-center">
                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mb-4">
                      <i className="ri-calendar-line text-brand-gray-400 text-2xl" />
                    </div>
                    <p className="font-sans text-brand-gray-500 text-[15px]">
                      Selecciona una fecha disponible en el calendario para ver los horarios.
                    </p>
                  </div>
                )}

                {step === 'form' && selectedDate && (
                  <>
                    {/* Selected date summary */}
                    <div className="bg-brand-gray-50 rounded-[20px] p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-brand-black flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-white text-lg" />
                      </div>
                      <div>
                        <p className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider">
                          Fecha seleccionada
                        </p>
                        <p className="font-serif text-brand-black text-lg font-medium">
                          {selectedDate.getDate()} de {monthNames[selectedDate.getMonth()]} de {selectedDate.getFullYear()}
                        </p>
                      </div>
                      <button
                        onClick={() => { setStep('calendar'); setSelectedDate(null); setSelectedTime(''); }}
                        className="ml-auto w-8 h-8 rounded-lg bg-white flex items-center justify-center text-brand-gray-400 hover:text-brand-black transition-colors"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>

                    {/* Resumen del dia */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-brand-gray-50 rounded-xl p-3 flex items-center gap-2.5">
                        <i className="ri-time-line text-brand-gray-400 text-sm" />
                        <span className="font-sans text-brand-gray-600 text-sm">
                          <strong className="text-brand-black">{franjasDelDia.length}</strong> franjas ·{' '}
                          <strong className="text-brand-black">{totalFranjasDisponibles}</strong> disponibles
                        </span>
                      </div>
                    </div>

                    {/* Franjas horarias */}
                    {franjasDelDia.length > 0 ? (
                      <div className="space-y-5">
                        {franjasDelDia.map((franja) => (
                          <div key={franja.slotId}>
                            {/* Cabecera de franja */}
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium">
                                {franja.label}
                              </span>
                              {franja.isFull ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700">
                                  <i className="ri-close-line" />
                                  Completa
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
                                  <i className="ri-check-line" />
                                  {franja.disponibles} / {franja.max} {franja.disponibles === 1 ? 'plaza' : 'plazas'}
                                </span>
                              )}
                            </div>

                            {/* Botones de hora */}
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {franja.times.map((time) => (
                                <button
                                  key={time}
                                  onClick={() => !franja.isFull && setSelectedTime(time)}
                                  disabled={franja.isFull}
                                  className={`py-2.5 rounded-xl font-sans text-sm transition-all whitespace-nowrap ${
                                    selectedTime === time
                                      ? 'bg-brand-black text-white'
                                      : franja.isFull
                                        ? 'bg-brand-gray-100 text-brand-gray-300 cursor-not-allowed'
                                        : 'bg-brand-gray-50 text-brand-black hover:bg-brand-gray-200 cursor-pointer'
                                  }`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
                        <i className="ri-alert-line text-amber-600 mt-0.5" />
                        <p className="font-sans text-amber-700 text-sm">
                          No hay franjas horarias configuradas para este dia.
                        </p>
                      </div>
                    )}

                    {/* Service type */}
                    <div>
                      <label className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium mb-3 block">
                        Tipo de cita *
                      </label>
                      <div className="space-y-2">
                        {types
                          .filter((t) => t.is_active && (t.category === 'vestidos-novia' || !t.category))
                          .sort((a, b) => {
                            const order: Record<string, number> = {
                              'Consulta de novia': 1,
                              'Prueba de vestido de novia': 2,
                              'Prueba final de novia': 3,
                              'Entrega vestido de novia': 4,
                            };
                            return (order[a.name] || 99) - (order[b.name] || 99);
                          })
                          .map((type) => {
                            const priceNum = typeof type.price === 'string' ? parseFloat(type.price) : type.price;
                            const hasPrice = priceNum !== undefined && priceNum > 0;
                            return (
                              <button
                                key={type.id}
                                onClick={() => setSelectedService(type.name)}
                                className={`w-full text-left p-4 rounded-xl border transition-all ${
                                  selectedService === type.name
                                    ? 'border-brand-black bg-brand-black text-white'
                                    : 'border-brand-gray-200 bg-white text-brand-black hover:border-brand-gray-400'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedService === type.name ? 'bg-brand-sage' : 'bg-brand-gray-300'}`} />
                                    <span className="font-sans text-sm font-medium">{type.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {hasPrice && (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${selectedService === type.name ? 'bg-white/20 text-white' : 'bg-brand-sage/30 text-brand-black'}`}>
                                        €{priceNum!.toFixed(2)}
                                      </span>
                                    )}
                                    <span className={`font-sans text-xs ${selectedService === type.name ? 'text-white/60' : 'text-brand-gray-400'}`}>
                                      {type.duration_minutes} min
                                    </span>
                                  </div>
                                </div>
                                {type.description && (
                                  <p className={`font-sans text-xs mt-2 leading-relaxed ${selectedService === type.name ? 'text-white/70' : 'text-brand-gray-500'}`}>
                                    {type.description}
                                  </p>
                                )}
                                {hasPrice && (
                                  <p className={`font-sans text-[11px] mt-1.5 italic ${selectedService === type.name ? 'text-white/60' : 'text-brand-gray-400'}`}>
                                    Pago online obligatorio para reservar
                                  </p>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    </div>

                    {/* Personal data form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-1">
                        <label className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium">
                          Nombre completo *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                          className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black transition-colors"
                          placeholder="Tu nombre"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                            className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black transition-colors"
                            placeholder="tu@email.com"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium">
                            Teléfono
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
                            className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black transition-colors"
                            placeholder="+34 600 000 000"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-sans text-brand-gray-600 text-xs tracking-wider uppercase font-medium">
                          Notas adicionales
                        </label>
                        <textarea
                          rows={3}
                          maxLength={500}
                          value={formData.notes}
                          onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
                          className="w-full bg-white border border-brand-gray-200 rounded-xl px-4 py-3.5 font-sans text-brand-black text-sm focus:outline-none focus:border-brand-black transition-colors resize-none"
                          placeholder="Cuéntanos tu visión de vestido, fecha de boda estimada, inspiraciones..."
                        />
                        <p className="font-sans text-brand-gray-400 text-xs mt-1">Máximo 500 caracteres</p>
                      </div>

                      {errorMsg && (
                        <div className="bg-red-50 rounded-xl p-3 flex items-start gap-2">
                          <i className="ri-error-warning-line text-red-600 mt-0.5" />
                          <p className="font-sans text-red-600 text-sm">{errorMsg}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={submitting || !selectedTime || !selectedService}
                        className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:bg-brand-gray-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {submitting
                          ? requiresPayment
                            ? 'Redirigiendo a Stripe...'
                            : 'Enviando...'
                          : requiresPayment
                            ? `Pagar y Reservar · €${selectedType?.price?.toFixed(2)}`
                            : 'Confirmar Reserva'}
                      </button>

                      <p className="font-sans text-brand-gray-400 text-xs text-center leading-relaxed">
                        Al enviar, aceptas nuestra política de privacidad.
                        Tu información será tratada con la máxima confidencialidad.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}